import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef, useState } from 'react';
import { ArrowRight, ArrowUpRight, Check, Zap, Shield, Clock, Users, Send } from 'lucide-react';
import { SEO } from '@/components/common/SEO';
import { ServiceIcon } from '@/components/common/ServiceIcon';
import { useFeaturedTestimonials } from '@/hooks/useTestimonials';
import { useFeaturedCaseStudies } from '@/hooks/useCaseStudies';
import { useServices } from '@/hooks/useServices';
import { useTranslation } from '@/features/i18n';

export function HomePage() {
  const { data: testimonials } = useFeaturedTestimonials();
  const { data: caseStudies } = useFeaturedCaseStudies();
  const { data: services } = useServices();
  const { t } = useTranslation();
  const heroRef = useRef<HTMLDivElement>(null);

  // Contact form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    // Simulate form submission - replace with actual API call
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIsSubmitting(false);
    setSubmitSuccess(true);
    setFormData({ name: '', email: '', company: '', message: '' });
  };

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });

  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 0.9]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  // Benefits/features grid
  const benefits = [
    {
      icon: Zap,
      title: 'Rask implementering',
      description: 'Vi leverer løsninger raskt uten å gå på bekostning av kvalitet.',
    },
    {
      icon: Shield,
      title: 'Sikkerhet først',
      description: 'Alle våre løsninger er bygget med sikkerhet som førsteprioritet.',
    },
    {
      icon: Clock,
      title: '24/7 Support',
      description: 'Vi er alltid tilgjengelige når du trenger oss mest.',
    },
    {
      icon: Users,
      title: 'Dedikert team',
      description: 'Et team av eksperter som forstår din virksomhet.',
    },
  ];

  // FAQ items
  const faqItems = [
    {
      question: 'Hvor lang tid tar det å utvikle en app?',
      answer: 'Utviklingstiden varierer avhengig av kompleksiteten, men typisk tar det 2-4 måneder for en MVP og 4-8 måneder for en fullstendig løsning.',
    },
    {
      question: 'Hva koster det å få utviklet en nettside?',
      answer: 'Prisen avhenger av omfanget og funksjonaliteten. Vi tilbyr skreddersydde løsninger og gir alltid et detaljert tilbud etter en innledende samtale.',
    },
    {
      question: 'Tilbyr dere support etter lansering?',
      answer: 'Ja, vi tilbyr omfattende support og vedlikeholdsavtaler. Vi er her for å sikre at løsningen din fungerer optimalt over tid.',
    },
    {
      question: 'Kan dere integrere med eksisterende systemer?',
      answer: 'Absolutt! Vi har bred erfaring med integrasjoner mot ulike systemer som ERP, CRM, betalingsløsninger og mer.',
    },
  ];

  return (
    <>
      <SEO
        title={t.home.seo.title}
        description={t.home.seo.description}
      />

      {/* ========== HERO SECTION - NINITO EXACT STYLE ========== */}
      <section ref={heroRef} className="relative h-screen w-full bg-[#edf1f5] overflow-hidden">
        <motion.div
          style={{ scale: heroScale, opacity: heroOpacity }}
          className="absolute inset-0"
        >
          {/* Hero container with rounded corners - exact ninito style */}
          <div className="absolute inset-[112px_120px_60px] rounded-[28px] overflow-hidden bg-black max-lg:inset-[100px_40px_40px] max-md:inset-[80px_20px_20px] max-md:rounded-[20px]">
            {/* Background image/video placeholder */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460]">
              {/* Decorative elements */}
              <div className="absolute top-1/4 left-1/4 w-[400px] h-[400px] bg-[#0078ff]/20 rounded-full blur-[100px]" />
              <div className="absolute bottom-1/4 right-1/4 w-[300px] h-[300px] bg-[#00d4ff]/10 rounded-full blur-[80px]" />
            </div>

            {/* Hero content - grid layout with text and form */}
            <div className="absolute inset-0 flex items-center px-[80px] max-md:px-[24px]">
              <div className="w-full grid lg:grid-cols-2 gap-12 items-center">
                {/* Left side - Text content */}
                <div className="max-lg:pt-16">
                  <motion.h1
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="text-[40px] md:text-[48px] lg:text-[56px] font-semibold text-white leading-[1.1] tracking-[-0.03em]"
                    style={{ textShadow: '1px 3px 24px rgba(0,0,0,0.4)' }}
                  >
                    {t.home.hero.title}{' '}
                    <span className="text-[#00d4ff]">{t.home.hero.titleHighlight}</span>
                  </motion.h1>

                  <motion.p
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    className="mt-5 text-lg text-white/70 max-w-[500px] leading-relaxed"
                  >
                    {t.home.hero.description}
                  </motion.p>

                  <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                    className="mt-8 flex flex-wrap gap-4"
                  >
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-black font-medium text-lg hover:bg-white/90 transition-all group"
                    >
                      {t.home.hero.cta}
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                    <Link
                      to="/services"
                      className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/30 text-white font-medium text-lg hover:bg-white/10 transition-all"
                    >
                      {t.home.hero.secondaryCta}
                    </Link>
                  </motion.div>
                </div>

                {/* Right side - Contact Form */}
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.4 }}
                  className="max-lg:hidden"
                >
                  <div className="bg-white/10 backdrop-blur-md rounded-[20px] p-6 border border-white/20 max-w-[380px] ml-auto">
                    <h3 className="text-lg font-semibold text-white mb-1">{t.home.hero.form.title}</h3>
                    <p className="text-white/60 text-sm mb-5">{t.home.hero.form.subtitle}</p>

                    {submitSuccess ? (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center py-6"
                      >
                        <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-3">
                          <Check className="w-6 h-6 text-green-400" />
                        </div>
                        <h4 className="text-lg font-semibold text-white mb-1">{t.home.hero.form.successTitle}</h4>
                        <p className="text-white/60 text-sm">{t.home.hero.form.successMessage}</p>
                      </motion.div>
                    ) : (
                      <form onSubmit={handleFormSubmit} className="space-y-3">
                        <input
                          type="text"
                          name="name"
                          placeholder={t.home.hero.form.name}
                          value={formData.name}
                          onChange={handleFormChange}
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white text-sm placeholder:text-white/40 focus:outline-none focus:border-[#00d4ff] transition-colors"
                        />
                        <input
                          type="email"
                          name="email"
                          placeholder={t.home.hero.form.email}
                          value={formData.email}
                          onChange={handleFormChange}
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white text-sm placeholder:text-white/40 focus:outline-none focus:border-[#00d4ff] transition-colors"
                        />
                        <input
                          type="text"
                          name="company"
                          placeholder={t.home.hero.form.company}
                          value={formData.company}
                          onChange={handleFormChange}
                          className="w-full px-4 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white text-sm placeholder:text-white/40 focus:outline-none focus:border-[#00d4ff] transition-colors"
                        />
                        <textarea
                          name="message"
                          placeholder={t.home.hero.form.message}
                          value={formData.message}
                          onChange={handleFormChange}
                          rows={2}
                          required
                          className="w-full px-4 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white text-sm placeholder:text-white/40 focus:outline-none focus:border-[#00d4ff] transition-colors resize-none"
                        />
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-[#00d4ff] text-black text-sm font-semibold hover:bg-[#00d4ff]/90 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                          {isSubmitting ? (
                            <>
                              <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                              {t.home.hero.form.submitting}
                            </>
                          ) : (
                            <>
                              {t.home.hero.form.submit}
                              <Send className="w-4 h-4" />
                            </>
                          )}
                        </button>
                      </form>
                    )}
                  </div>
                </motion.div>
              </div>
            </div>

          </div>
        </motion.div>
      </section>

      {/* ========== TRUSTED BY SECTION ========== */}
      <section className="py-20 bg-white border-b border-black/5">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-20">
          <p className="text-center text-sm text-[#999] mb-12 uppercase tracking-wider font-medium">
            {t.home.partners}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-20 gap-y-10">
            {[1, 2, 3, 4].map((num) => (
              <img
                key={num}
                src={`/partner/${num}.png`}
                alt={`Partner ${num}`}
                className={`h-20 md:h-24 lg:h-28 w-auto object-contain ${num === 1 ? 'brightness-0' : ''}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ========== STICKY FEATURE CARDS - NINITO EXACT STYLE ========== */}
      <section className="bg-[#edf1f5] py-10 pb-40">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-20">
          {/* Section header */}
          <div className="text-center mb-20">
            <span className="text-sm font-medium text-[#0078ff] uppercase tracking-wider">
              Våre tjenester
            </span>
            <h2 className="mt-4 text-[40px] md:text-[48px] lg:text-[56px] font-semibold text-black tracking-[-0.02em]">
              {t.home.services.title}
            </h2>
            <p className="mt-5 text-xl text-[#666] max-w-[600px] mx-auto">
              {t.home.services.subtitle}
            </p>
          </div>

          {/* Sticky cards */}
          {services?.map((service, index) => (
            <motion.div
              key={service.slug}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6 }}
              className="sticky mb-10"
              style={{
                top: `${130 + index * 20}px`,
                zIndex: index + 1
              }}
            >
              <Link to={`/services/${service.slug}`} className="block group">
                <div className="bg-white rounded-[28px] overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.06)] hover:shadow-[0_12px_48px_rgba(0,0,0,0.12)] transition-all duration-300">
                  <div className="flex flex-col lg:flex-row min-h-[500px] lg:min-h-[70vh] lg:max-h-[600px]">
                    {/* Content side */}
                    <div className="flex-1 p-10 lg:p-16 flex flex-col justify-center">
                      <div className="w-16 h-16 rounded-2xl bg-[#0078ff]/10 flex items-center justify-center mb-8">
                        <ServiceIcon icon={service.icon} size="lg" className="text-[#0078ff]" />
                      </div>

                      <h3 className="text-[32px] md:text-[40px] lg:text-[48px] font-semibold text-black tracking-[-0.02em] group-hover:text-[#0078ff] transition-colors">
                        {service.title}
                      </h3>

                      <p className="mt-5 text-lg text-[#666] leading-relaxed max-w-[500px]">
                        {service.short_description}
                      </p>

                      {service.features_json && service.features_json.length > 0 && (
                        <div className="mt-8 grid grid-cols-2 gap-3">
                          {service.features_json.slice(0, 4).map((feature, i) => (
                            <div key={i} className="flex items-center gap-3">
                              <div className="w-6 h-6 rounded-lg bg-[#f2f2f2] flex items-center justify-center flex-shrink-0">
                                <Check className="w-3.5 h-3.5 text-black" />
                              </div>
                              <span className="text-[#666] text-sm">{feature.title}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="mt-10">
                        <span className="inline-flex items-center gap-2 text-[#0078ff] font-medium text-lg group-hover:gap-3 transition-all">
                          Les mer
                          <ArrowUpRight className="w-5 h-5" />
                        </span>
                      </div>
                    </div>

                    {/* Image side */}
                    <div className="flex-1 bg-gradient-to-br from-[#f8f9fa] to-[#edf1f5] flex items-center justify-center p-10 lg:p-0 relative overflow-hidden">
                      {/* Decorative gradient */}
                      <div className="absolute inset-0 bg-gradient-to-br from-[#0078ff]/5 to-transparent" />
                      <div className="relative w-full max-w-[400px] aspect-square flex items-center justify-center">
                        {/* Large number */}
                        <div className="text-[160px] lg:text-[200px] font-bold text-black/[0.03] select-none">
                          {String(index + 1).padStart(2, '0')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}

          {/* View all services button */}
          <div className="text-center mt-16 relative z-10">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-black text-white font-medium text-lg hover:bg-black/90 transition-all group"
            >
              {t.home.services.viewAll}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* ========== QUOTE SECTION ========== */}
      <section className="py-24 bg-white">
        <div className="max-w-[900px] mx-auto px-5 md:px-10 lg:px-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <p className="text-[28px] md:text-[36px] lg:text-[42px] font-medium text-black leading-tight tracking-[-0.02em]">
              "{t.home.intro.prefix} <span className="text-[#0078ff]">{t.home.intro.highlight1}</span> {t.home.intro.middle}{' '}
              <span className="text-[#0078ff]">{t.home.intro.highlight2}</span> {t.home.intro.suffix}"
            </p>
          </motion.div>
        </div>
      </section>

      {/* ========== BENEFITS GRID ========== */}
      <section className="py-24 bg-[#edf1f5]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-20">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-[#0078ff] uppercase tracking-wider">
              Hvorfor velge oss
            </span>
            <h2 className="mt-4 text-[40px] md:text-[48px] font-semibold text-black tracking-[-0.02em]">
              {t.home.whyUs.title}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-7">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="bg-white rounded-[28px] p-8 h-full text-center hover:shadow-[0_12px_48px_rgba(0,0,0,0.08)] transition-all">
                  <div className="w-16 h-16 rounded-2xl bg-[#0078ff]/10 flex items-center justify-center mx-auto mb-6">
                    <benefit.icon className="w-8 h-8 text-[#0078ff]" />
                  </div>
                  <h3 className="text-xl font-semibold text-black mb-3">{benefit.title}</h3>
                  <p className="text-[#666] leading-relaxed">{benefit.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== WHY US SECTION ========== */}
      <section className="py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm font-medium text-[#0078ff] uppercase tracking-wider">
                Om oss
              </span>
              <h2 className="mt-4 text-[40px] md:text-[48px] font-semibold text-black tracking-[-0.02em]">
                Erfaring og ekspertise du kan stole på
              </h2>
              <p className="mt-5 text-lg text-[#666] leading-relaxed">
                {t.home.whyUs.description}
              </p>

              <div className="mt-10 space-y-5">
                {t.home.whyUs.points.map((point, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start gap-4"
                  >
                    <div className="w-6 h-6 rounded-full bg-[#0078ff]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-[#0078ff]" />
                    </div>
                    <span className="text-[#666] leading-relaxed">{point}</span>
                  </motion.div>
                ))}
              </div>

              <div className="mt-12">
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-black text-white font-medium hover:bg-black/90 transition-all group"
                >
                  Les mer om oss
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-square rounded-[28px] bg-[#edf1f5] p-10 lg:p-16 flex flex-col justify-center items-center text-center relative overflow-hidden">
                {/* Decorative */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#0078ff]/5 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#0078ff]/5 rounded-full blur-2xl" />

                <div className="relative">
                  <div className="text-[80px] lg:text-[120px] font-bold text-black tracking-tight leading-none">
                    5+
                  </div>
                  <div className="mt-4 text-xl text-[#666]">{t.home.whyUs.experience}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== CASE STUDIES ========== */}
      {caseStudies && caseStudies.length > 0 && (
        <section className="py-24 bg-[#edf1f5]">
          <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-20">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
              <div>
                <span className="text-sm font-medium text-[#0078ff] uppercase tracking-wider">
                  {t.nav.caseStudies}
                </span>
                <h2 className="mt-4 text-[40px] md:text-[48px] font-semibold text-black tracking-[-0.02em]">
                  {t.home.caseStudies.title}
                </h2>
              </div>
              <Link
                to="/case-studies"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-black text-white font-medium hover:bg-black/90 transition-all self-start md:self-auto"
              >
                {t.home.caseStudies.viewAll}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
              {caseStudies.map((caseStudy, index) => (
                <motion.div
                  key={caseStudy.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Link to={`/case-studies/${caseStudy.slug}`} className="block h-full group">
                    <div className="bg-white rounded-[28px] overflow-hidden h-full hover:shadow-[0_12px_48px_rgba(0,0,0,0.1)] transition-all">
                      {caseStudy.cover_image_url && (
                        <div className="relative aspect-[16/10] overflow-hidden">
                          <img
                            src={caseStudy.cover_image_url}
                            alt={caseStudy.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                      )}
                      <div className="p-7">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#0078ff]/10 text-[#0078ff]">
                          {caseStudy.industry}
                        </span>
                        <h3 className="mt-4 text-xl font-semibold text-black tracking-tight group-hover:text-[#0078ff] transition-colors">
                          {caseStudy.title}
                        </h3>
                        <p className="mt-2 text-[#666]">{caseStudy.client_name}</p>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ========== TESTIMONIALS ========== */}
      {testimonials && testimonials.length > 0 && (
        <section className="py-24 bg-white">
          <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-20">
            <div className="text-center mb-16">
              <span className="text-sm font-medium text-[#0078ff] uppercase tracking-wider">
                {t.home.testimonials.title}
              </span>
              <h2 className="mt-4 text-[40px] md:text-[48px] font-semibold text-black tracking-[-0.02em]">
                {t.home.testimonials.subtitle}
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="bg-[#edf1f5] rounded-[28px] p-8 h-full">
                    <div className="flex gap-1 mb-5">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <svg key={i} className="w-5 h-5 text-amber-400 fill-current" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    <blockquote className="text-lg text-black leading-relaxed mb-6">
                      "{testimonial.quote}"
                    </blockquote>
                    <div className="flex items-center gap-4">
                      {testimonial.avatar_url ? (
                        <img
                          src={testimonial.avatar_url}
                          alt={testimonial.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center">
                          <span className="text-[#666] font-medium text-lg">
                            {testimonial.name[0]}
                          </span>
                        </div>
                      )}
                      <div>
                        <div className="font-semibold text-black">{testimonial.name}</div>
                        <div className="text-[#666] text-sm">
                          {testimonial.role}
                          {testimonial.company && `, ${testimonial.company}`}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ========== FAQ SECTION ========== */}
      <section className="py-24 bg-[#edf1f5]">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-20">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <span className="text-sm font-medium text-[#0078ff] uppercase tracking-wider">
                FAQ
              </span>
              <h2 className="mt-4 text-[40px] md:text-[48px] font-semibold text-black tracking-[-0.02em]">
                Vanlige spørsmål
              </h2>
              <p className="mt-5 text-lg text-[#666] leading-relaxed">
                Har du spørsmål? Her finner du svar på de vanligste spørsmålene vi får.
              </p>
              <div className="mt-10">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-black text-white font-medium hover:bg-black/90 transition-all group"
                >
                  Kontakt oss
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            <div className="space-y-4">
              {faqItems.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-[20px] p-6"
                >
                  <h3 className="text-lg font-semibold text-black mb-3">{item.question}</h3>
                  <p className="text-[#666] leading-relaxed">{item.answer}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== CTA SECTION - DARK ========== */}
      <section className="relative bg-black py-32 overflow-hidden">
        {/* Background glow */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#0078ff]/20 rounded-full blur-[120px]" />
        </div>

        <div className="relative max-w-[1200px] mx-auto px-5 md:px-10 lg:px-20 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-[40px] md:text-[48px] lg:text-[56px] font-semibold text-white tracking-[-0.02em]"
          >
            {t.home.cta.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mt-6 text-xl text-white/60 max-w-[600px] mx-auto"
          >
            {t.home.cta.description}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-black font-medium text-lg hover:bg-white/90 transition-all group"
            >
              {t.home.cta.button}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/20 text-white font-medium text-lg hover:bg-white/10 transition-all"
            >
              {t.home.cta.secondaryButton}
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
